/**
 * tickVehicles — pure helper extracted from coreSlice.nextDay
 * (03/06/2026 round 3 refactor of the vehicles block).
 *
 * Drives the daily lifecycle of each owned vehicle :
 *   • Accumulate km / wear / daysActive / totalRevenue on working days
 *     when the vehicle is on an active mission.
 *   • Auto-flip `delivery` → `idle` when totalDays >= availableOnDay
 *     (3-day vehicle delivery cycle).
 *   • Auto-flip `selling` → `sold` when totalDays >= saleCompletionDay
 *     (5-day vehicle resale cycle).
 *   • Mark `leaseExpiring: true` when leaseEndDay is reached.
 *   • Reset assignment when the contract just completed.
 *   • Random breakdown roll on wear ≥ 90 (halved by `diag_preventif`).
 *
 * @param {Object} state
 * @param {boolean} working
 * @param {Array<string>} completedContractIds - contracts ending this tick
 * @param {Object} opts
 * @param {boolean} opts.hasDiagPreventif - true when the player unlocked
 *   Bruno Lavergne's preventive diagnostics (halves breakdown probability)
 * @param {Array<{id:string,kmPerDay:number}>} opts.vehicleSpecs - VEHICLES catalog
 *   subset, indexed by `dataId`; the helper uses it to look up kmPerDay.
 * @returns {{
 *   updatedVehicles: Array,
 *   alerts: Array,
 *   breakdownsThisTick: number,
 *   deliveredVehicles: Array,
 *   staleDriverIds: Array<string>  // 17/06/2026 — drivers à détacher
 *     (cf. healer P0). Le caller doit clear `assignedVehicleId` pour ces
 *     IDs pour finaliser la réconciliation côté drivers.
 * }}
 */
import { VEHICLES } from '../../data/vehicles';
import { findCityById, haversineKm } from '../../data/mapCities';
import { getDepotCityId } from '../../data/depotGeo';

export function tickVehicles(state, working, completedContractIds = [], opts = {}) {
  const hasDiagPreventif = !!opts.hasDiagPreventif;
  const completedSet = new Set(completedContractIds);
  // 04/02/2026 — Phase Q1 : contrats ponctuels terminés → 1 jour de retour
  // pour le véhicule (status 'returning', réutilise availableOnDay).
  const completedPunctualSet = new Set(opts.completedPunctualIds || []);
  const alerts = [];
  // 09/02/2026 — Q2 Lot I user prod : popups décisionnels en cas de panne.
  // Permet d'avoir une vraie visibilité (popup forcée) + un choix
  // (Réparer / Sinistre / Hors service). Cf. eventSlice.resolveEvent.
  const breakdownPopups = [];
  const deliveredVehicles = []; // 04/06/2026 — collected for inbox emails
  // 09/02/2026 — Q2 Lot I user prod : véhicules dont le retour de mission
  // déclenche un rappel « pendingRepair » (issu de maintainAllVehicles).
  const pendingRepairReturns = [];
  // 17/06/2026 — drivers à détacher après le map (set commun, appliqué par
  // coreSlice via `staleDriverIds`). Cf. P0 bug user : camion + chauffeur
  // restés immobilisés sans raison. Cause racine : un véhicule conservait
  // `assignedDriverId` vers un chauffeur en congés / arrêt / grève / formation
  // / disparu, ce qui empêchait toute nouvelle assignation (UI cachée si
  // `assignedDriverId` est tronqué).
  const staleDriverIds = new Set();
  const _driverById = Object.fromEntries((state.drivers || []).map(d => [d.id, d]));
  let breakdownsThisTick = 0;

  const updatedVehicles = state.vehicles.map(v => {
    const newV = { ...v };
    // P0 healer — auto-détache un binding chauffeur stale.
    // Cas couverts :
    //  • chauffeur absent du référentiel `state.drivers` (déjà licencié)
    //  • chauffeur indisponible (congés / arrêt / grève / formation)
    //    ET véhicule pas en mission active (status !== 'active')
    if (newV.assignedDriverId) {
      const drv = _driverById[newV.assignedDriverId];
      // 10/02/2026 (Lot L) — Retour user prod : « le chauffeur doit
      // revenir sur le contrat pour lequel il était attribué lors de son
      // retour de congés ». Les congés sont temporaires (5 j) — on ne
      // doit PAS rompre le binding bidirectionnel chauffeur↔véhicule
      // sinon l'assignation est perdue au retour. On exclut donc
      // `onHoliday` de la liste `blocking` (les autres absences longues
      // — sick, training, striking, vacation — restent traitées par le
      // healer).
      // 12/02/2026 (fix user prod « sous-traitance depuis messagerie
      // n'a pas pris effet ») — On préserve aussi le binding pendant
      // toute la fenêtre `subcontracted` : un intérimaire prend la
      // place, le contrat continue, et le chauffeur titulaire retrouve
      // son véhicule au retour de congés/vacances.
      const today = state.totalDays || 0;
      const vehicleUnderSubcontract = newV.subcontracted === true
        && (newV.subcontractUntil || 0) > today;
      const blocking = drv && !vehicleUnderSubcontract && (
        drv.status === 'vacation'
        || drv.status === 'sick'
        || drv.status === 'striking'
        || drv.status === 'training'
      );
      if (!drv) {
        newV.assignedDriverId = null;
        alerts.push({
          type: 'stale_assignment_healed',
          message: `${newV.brand || ''} ${newV.model || ''} : chauffeur fantôme retiré (auto-correction).`,
          severity: 'info',
        });
      } else if (blocking && newV.status !== 'active' && newV.status !== 'transfer') {
        // Véhicule libre mais chauffeur indisponible : on détache pour
        // permettre la ré-assignation manuelle.
        newV.assignedDriverId = null;
        staleDriverIds.add(drv.id);
        const _driverLabel = drv.name || drv.firstName || 'le chauffeur';
        const _statusLabel = drv.status === 'training'
          ? 'formation'
          : drv.status === 'sick'
            ? 'arrêt maladie'
            : drv.status === 'striking'
              ? 'grève'
              : drv.status || 'indisponible';
        alerts.push({
          type: 'stale_assignment_healed',
          message: `${(newV.brand || '').trim()} ${(newV.model || '').trim()} libéré : ${_driverLabel} indisponible (${_statusLabel}).`.replace(/\s+/g, ' ').trim(),
          severity: 'info',
        });
      }
    }
    // Accumulate km/wear/revenue when active.
    // 12/02/2026 — Retour user prod : « L'usure doit être alignée sur les
    // km parcourus plutôt qu'un forfait/jour ». On lit d'abord la distance
    // du contrat en cours (source : contract.distanceKm), avec repli sur
    // le `kmPerDay` catalogue si absent. L'usure devient linéaire aux km
    // (~1 point / 100 km) — un T5 sur du 500 km/jour s'use ~5×/j, un
    // urbain 80 km/j ~0,8×/j.
    if (working && (v.assignedContractId || v.status === 'active')) {
      const vehicleData = VEHICLES.find(vd => vd.id === v.dataId);
      const contract = state.activeContracts.find(c => c.instanceId === v.assignedContractId);
      const contractKm = contract && Number.isFinite(contract.distanceKm) ? contract.distanceKm : null;
      const dailyKm = contractKm != null ? contractKm : (vehicleData?.kmPerDay || 180);
      newV.km = (v.km || 0) + dailyKm;
      // 18/07/2026 — user prod : « l'usure est trop élevée, il faut la
      // réduire ». Neuf : −50 % (0,005 pt/km ≈ 0,9 pt/j à 180 km/j).
      // Occasion (isUsed) : ×1,6 (0,008 pt/km ≈ 1,4 pt/j — reste sous
      // l'ancien 0,01). Jitter ±10 % conservé pour la variabilité.
      const wearPerKm = v.isUsed ? 0.008 : 0.005;
      const wearIncrement = dailyKm * wearPerKm * (0.9 + Math.random() * 0.2);
      newV.wear = Math.min(100, (v.wear || 0) + wearIncrement);
      newV.daysActive = (v.daysActive || 0) + 1;
      if (contract) {
        newV.totalRevenue = (v.totalRevenue || 0) + (contract.dailyRevenue || 0);
      }
      // 22/07/2026 — stats période pour le rapport Flotte de Rémi Forni
      // (q* = trimestre, y* = exercice ; reset par coreSlice aux clôtures).
      newV.qActiveDays = (v.qActiveDays || 0) + 1;
      newV.yActiveDays = (v.yActiveDays || 0) + 1;
      newV.qFuelCost = (v.qFuelCost || 0) + (v.dailyFuel || 0);
      newV.yFuelCost = (v.yFuelCost || 0) + (v.dailyFuel || 0);
      if (contract) {
        newV.qRevenue = (v.qRevenue || 0) + (contract.dailyRevenue || 0);
        newV.yRevenue = (v.yRevenue || 0) + (contract.dailyRevenue || 0);
      }
    }
    // Release vehicle whose contract just completed.
    if (completedSet.has(v.assignedContractId)) {
      // 04/02/2026 — Phase Q1 : si mission ponctuelle, le véhicule rentre
      // au dépôt pendant 1 jour (status 'returning') au lieu d'être immé-
      // diatement réassignable. Cf. retour user prod : « si un chauffeur
      // fait une mission ponctuelle un peu loin il doit pas être dispo
      // le lendemain matin ».
      if (completedPunctualSet.has(v.assignedContractId)) {
        newV.status = 'returning';
        newV.returningUntilDay = (state.totalDays || 0) + 2; // dispo dès J+2
      } else {
        newV.status = 'idle';
      }
      newV.assignedContractId = null;
      // 09/02/2026 — Q2 Lot I user prod : si le véhicule avait été marqué
      // pendingRepair lors d'un « Tout réparer » alors qu'il était en
      // mission, on signale son retour pour rappel.
      if (v.pendingRepair) {
        pendingRepairReturns.push({ id: newV.id, brand: v.brand, model: v.model });
      }
    }
    // 04/02/2026 — Phase Q1 : passage 'returning' → 'idle' une fois le jour
    // de retour écoulé. Pattern identique à 'delivery' / 'transfer'.
    if (newV.status === 'returning' && typeof newV.returningUntilDay === 'number') {
      if ((state.totalDays + 1) >= newV.returningUntilDay) {
        newV.status = 'idle';
        newV.returningUntilDay = null;
      }
    }
    // 3-day delivery cycle: flip 'delivery' → 'idle'.
    if (newV.status === 'delivery' && typeof newV.availableOnDay === 'number') {
      if ((state.totalDays + 1) >= newV.availableOnDay) {
        newV.status = 'idle';
        // 04/06/2026 — Track delivery event so coreSlice can push an
        // inbox email + a top-bar notification (user request: "Il
        // faudrait un email pour savoir que le véhicule commandé est
        // enfin arrivé").
        deliveredVehicles.push({ id: newV.id, brand: newV.brand, model: newV.model, isLeased: !!newV.isLeased });
      }
    }
    // 5-day resale cycle: flip 'selling' → 'sold' (filtered out later).
    if (newV.status === 'selling' && typeof newV.saleCompletionDay === 'number') {
      if ((state.totalDays + 1) >= newV.saleCompletionDay) {
        newV.status = 'sold';
      }
    }
    // 13/06/2026 — Chantier Dépôts Phase 3 : arrivée d'un transfert
    // inter-dépôts (1 jour de route). Le camion rejoint son nouveau site.
    if (newV.status === 'transfer' && typeof newV.transferArrivalDay === 'number') {
      if ((state.totalDays + 1) >= newV.transferArrivalDay) {
        newV.status = 'idle';
        newV.depotId = newV.transferTargetDepotId || newV.depotId;
        newV.transferArrivalDay = null;
        newV.transferTargetDepotId = null;
        alerts.push({
          type: 'transfer_done',
          message: `${newV.brand} ${newV.model} est arrivé à son nouveau dépôt.`,
          severity: 'info',
        });
      }
    }
    if (v.isLeased && v.leaseEndDay && state.totalDays >= v.leaseEndDay) {
      newV.leaseExpiring = true;
    }
    // 07/02/2026 — P0 retour user : alertes de seuil sur l'usure (70 % et
    // 85 %). One-shot par véhicule (drapeau wearAlertSent à 70/85). Permet
    // au joueur de programmer un entretien avant la zone critique 90 %+.
    const prevAlert = v.wearAlertSent || null;
    if (newV.wear >= 70 && newV.wear < 85 && prevAlert !== 'mid' && prevAlert !== 'high') {
      newV.wearAlertSent = 'mid';
      alerts.push({
        type: 'wear_warning',
        message: `${v.brand} ${v.model} : usure ${Math.round(newV.wear)} % (seuil 70 %). Programme un entretien préventif avant la zone critique.`,
        severity: 'warn',
        vehicleId: newV.id,
      });
    } else if (newV.wear >= 85 && prevAlert !== 'high') {
      newV.wearAlertSent = 'high';
      alerts.push({
        type: 'wear_critical',
        message: `${v.brand} ${v.model} : usure ${Math.round(newV.wear)} % — risque de panne réel. Entretien fortement recommandé.`,
        severity: 'grave',
        vehicleId: newV.id,
      });
    } else if (newV.wear < 60 && prevAlert) {
      // Reset après entretien (la jauge est repassée sous le seuil de réarmement).
      newV.wearAlertSent = null;
    }

    // 07/02/2026 — P0 retour user : risque d'incident moteur SCALÉ avec
    // l'usure (avant : seuil binaire à 90 % + roll plat 4-8 %). Désormais
    // probabilité croissante dès 70 % pour incentiver les entretiens
    // préventifs et pas seulement quand la jauge frôle 100 %.
    //   70-80 % → 1.0 %/j
    //   80-90 % → 3.0 %/j
    //   90-95 % → 6.0 %/j
    //   ≥ 95 % → 10  %/j
    // diag_preventif divise la prob par 2 (inchangé).
    let breakdownRoll = 0;
    if (newV.wear >= 95) breakdownRoll = 0.10;
    else if (newV.wear >= 90) breakdownRoll = 0.06;
    else if (newV.wear >= 80) breakdownRoll = 0.03;
    else if (newV.wear >= 70) breakdownRoll = 0.01;
    if (hasDiagPreventif) breakdownRoll *= 0.5;
    if (newV.status !== 'delivery' && breakdownRoll > 0 && Math.random() < breakdownRoll) {
      newV.status = 'broken';
      const severity = newV.wear >= 90 ? 'grave' : 'warn';
      alerts.push({ type: 'breakdown', message: `${v.brand} ${v.model} est en panne ! Usure ${Math.round(newV.wear)} %.`, severity, vehicleId: newV.id });
      // 09/02/2026 — Q2 Lot I user prod : le bandeau "alerts" était trop
      // discret (souvent invisible si l'apprenant n'est pas sur le
      // Dashboard). On pousse aussi un événement explicite dans pendingEvents
      // pour forcer une popup décisionnelle (Réparer / Sinistre / Hors service).
      breakdownPopups.push({
        id: `evt-breakdown-${newV.id}-${state.totalDays}`,
        instanceId: `evt-breakdown-${newV.id}-${state.totalDays}`,
        name: `Panne — ${v.brand} ${v.model}`,
        category: 'Panne mécanique',
        severity: newV.wear >= 90 ? 'critique' : 'modéré',
        durationDays: 0, costMin: 0, costMax: 0,
        revenueEffect: 'Immobilisation jusqu\'à réparation',
        reputationEffect: 0,
        description:
          `Votre ${v.brand} ${v.model} (usure ${Math.round(newV.wear)} %) vient de tomber en panne. ` +
          `Choisissez l'action :\n` +
          `• **Réparer immédiatement** au garage (paiement immédiat, dispo sous 1 j)\n` +
          `• **Déclarer un sinistre** à l'assurance (franchise selon contrat)\n` +
          `• **Mettre hors service** temporairement (gratuit, immobilisé jusqu'à intervention)`,
        vehicleId: newV.id,
        choices: [
          { label: '🔧 Réparer (atelier)', cost: 0, reputationChange: 0, description: 'Coût réel calculé au démarrage de la réparation' },
          { label: '🛡️ Déclarer un sinistre', cost: 0, reputationChange: 0, description: 'Franchise selon contrat (Basique / Standard / Premium)' },
          { label: '⏸️ Mettre hors service', cost: 0, reputationChange: 0, description: 'Immobilisation gratuite, à réparer plus tard' },
        ],
      });
      breakdownsThisTick++;
    }
    // 22/07/2026 — rapport Flotte Forni : jours immobilisés + coûts fixes
    // au prorata quotidien (entretien, assurance, loyer leasing / mensualité
    // de financement). Accrue tous les jours hors livraison/vente.
    if (working && (newV.status === 'maintenance' || newV.status === 'broken')) {
      newV.qDownDays = (v.qDownDays || 0) + 1;
      newV.yDownDays = (v.yDownDays || 0) + 1;
    }
    if (newV.status !== 'delivery' && newV.status !== 'sold') {
      const _fixedDaily = ((v.maintenancePerMonth || 0) + (v.insurancePerMonth || 0)
        + (v.isLeased ? (v.leaseMonthlyPayment || 0) : 0) + (v.monthlyPayment || 0)) / 30;
      newV.qFixedCost = (v.qFixedCost || 0) + _fixedDaily;
      newV.yFixedCost = (v.yFixedCost || 0) + _fixedDaily;
    }
    return newV;
  });

  // 17/06/2026 — Refonte dépôts user : retour auto vers le dépôt le plus
  // proche si le dépôt d'origine est saturé après une mission. Coûts de
  // transfert prélevés sur le résultat de l'opération (cf. coreSlice).
  // On retourne `autoReturns: [{ vehicleId, fromDepotId, toDepotId, cost }]`
  // pour que coreSlice débite la trésorerie et notifie.
  const autoReturns = [];
  const ownedDepots = state.ownedDepots || [];
  if (ownedDepots.length > 1) {
    const _getOccupancy = (depotId, vehicles) => {
      const contractById = Object.fromEntries((state.activeContracts || []).map(c => [c.instanceId, c]));
      return vehicles.filter(v => {
        if (v.status === 'sold') return false;
        if ((v.depotId || 'depot-hq') !== depotId) return false;
        if (v.assignedContractId) {
          const c = contractById[v.assignedContractId];
          if (c && c.frequency === 'régulier') return false;
        }
        return true;
      }).length;
    };
    completedSet.forEach(cid => {
      if (!cid) return;
      const v = updatedVehicles.find(uv => uv.status === 'idle' && uv.assignedContractId === null
        && state.vehicles.find(orig => orig.id === uv.id && orig.assignedContractId === cid));
      if (!v) return;
      const homeDepotId = v.depotId || 'depot-hq';
      const homeDepot = ownedDepots.find(d => d.id === homeDepotId);
      if (!homeDepot) return;
      const capacity = homeDepot.capacityVehicles || 10;
      if (_getOccupancy(homeDepotId, updatedVehicles) <= capacity) return;
      const fromCity = findCityById(getDepotCityId(homeDepot, state.company));
      let best = null;
      ownedDepots.forEach(d => {
        if (d.id === homeDepotId) return;
        const cap = d.capacityVehicles || 10;
        if (_getOccupancy(d.id, updatedVehicles) >= cap) return;
        const toCity = findCityById(getDepotCityId(d, state.company));
        if (!fromCity || !toCity) return;
        const dist = haversineKm(fromCity, toCity);
        if (!best || dist < best.distanceKm) {
          best = { depotId: d.id, distanceKm: dist };
        }
      });
      if (!best) return;
      const cost = Math.max(300, Math.round(best.distanceKm * 1.2));
      autoReturns.push({
        vehicleId: v.id,
        vehicleLabel: `${v.brand || ''} ${v.model || ''}`.trim(),
        fromDepotId: homeDepotId,
        toDepotId: best.depotId,
        cost,
      });
    });
  }

  return { updatedVehicles, alerts, breakdownsThisTick, deliveredVehicles, staleDriverIds: [...staleDriverIds], autoReturns, breakdownPopups, pendingRepairReturns };
}
